import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientMessage, ConnectionStatus, RoomState, ServerMessage, ThrowEvent } from './types';

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10_000;
const KEEPALIVE_MS = 20_000;

const socketUrl = (): string => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
};

type Identity = { clientId: string; name: string; spectator: boolean };

type UseRoom = {
  room: RoomState | null;
  youId: string | null;
  status: ConnectionStatus;
  error: string | null;
  /** Projectiles currently in flight; the animation layer drops them when done. */
  throws: ThrowEvent[];
  retireThrow: (id: string) => void;
  send: (message: ClientMessage) => void;
  dismissError: () => void;
};

/**
 * Owns the room WebSocket: joins on connect, reconnects with backoff, and keeps
 * the latest server snapshot. The socket is deliberately re-established rather
 * than diffed — the server always sends whole-room state, so a reconnect is a
 * full recovery.
 */
export const useRoom = (roomId: string | null, identity: Identity | null): UseRoom => {
  const [room, setRoom] = useState<RoomState | null>(null);
  const [youId, setYouId] = useState<string | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [throws, setThrows] = useState<ThrowEvent[]>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const identityRef = useRef<Identity | null>(identity);
  const attemptsRef = useRef(0);
  const reconnectRef = useRef<number | undefined>(undefined);
  const keepaliveRef = useRef<number | undefined>(undefined);
  const closedByUsRef = useRef(false);

  identityRef.current = identity;

  const send = useCallback((message: ClientMessage) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  }, []);

  useEffect(() => {
    if (!roomId || !identity) return;
    closedByUsRef.current = false;

    const connect = () => {
      if (closedByUsRef.current) return;
      setStatus((current) => (current === 'online' ? current : 'connecting'));

      const socket = new WebSocket(socketUrl());
      socketRef.current = socket;

      socket.onopen = () => {
        attemptsRef.current = 0;
        const current = identityRef.current;
        if (!current) return;
        socket.send(
          JSON.stringify({
            t: 'join',
            roomId,
            clientId: current.clientId,
            name: current.name,
            spectator: current.spectator,
          } satisfies ClientMessage),
        );
        setStatus('online');
        // Some proxies drop idle upgrades; a periodic ping keeps the path warm.
        keepaliveRef.current = window.setInterval(
          () => socket.readyState === WebSocket.OPEN && socket.send(JSON.stringify({ t: 'ping' })),
          KEEPALIVE_MS,
        );
      };

      socket.onmessage = (event) => {
        let message: ServerMessage;
        try {
          message = JSON.parse(event.data as string);
        } catch {
          return;
        }
        if (message.t === 'state') {
          setRoom(message.room);
          setYouId(message.youId);
        } else if (message.t === 'throw') {
          const { id, fromId, toId, emoji } = message;
          // Cap the queue so a burst can't pile up unbounded in a background tab.
          setThrows((current) => [...current, { id, fromId, toId, emoji }].slice(-24));
        } else if (message.t === 'error') {
          setError(message.message);
        } else if (message.t === 'kicked') {
          closedByUsRef.current = true;
          setStatus('kicked');
        }
      };

      socket.onclose = () => {
        window.clearInterval(keepaliveRef.current);
        if (closedByUsRef.current) return;
        setStatus('offline');
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** attemptsRef.current, RECONNECT_MAX_MS);
        attemptsRef.current += 1;
        reconnectRef.current = window.setTimeout(connect, delay);
      };

      socket.onerror = () => socket.close();
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      window.clearTimeout(reconnectRef.current);
      window.clearInterval(keepaliveRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
    // Only the room and the browser identity should force a new socket; name and
    // spectator changes are pushed over the existing one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, identity?.clientId]);

  // A tab woken from background sleep often has a socket that is closed but
  // never fired onclose. Nudge a reconnect when the user comes back.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ t: 'ping' }));
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const dismissError = useCallback(() => setError(null), []);
  const retireThrow = useCallback(
    (id: string) => setThrows((current) => current.filter((entry) => entry.id !== id)),
    [],
  );

  return { room, youId, status, error, throws, retireThrow, send, dismissError };
};
